import React, { type ChangeEvent, useMemo } from 'react';
import {
  Box,
  Checkbox as DSCheckbox,
  Icon,
  InlineMessage,
} from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import { testIds, VALIDATION_TYPES } from '../../../../shared/constants';
import {
  type CheckboxField,
  type DataProvider,
  type ProviderOption,
} from '../../../../shared/types';

import styles from './PreviewFields.module.css';

export interface CheckboxFieldProps {
  field: CheckboxField;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  value?: string[];
  onChange?: (newValues: string[]) => void;
  dataProvider?: DataProvider;
}

// Local render type and mapper
interface RenderCheckboxOption {
  value: string;
  label: string;
}

const mapProviderOption = (o: ProviderOption): RenderCheckboxOption => ({
  value: String(o.value),
  label: o.label,
});

function Checkbox({
  field,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
  value = [],
  onChange,
  dataProvider,
}: CheckboxFieldProps) {
  const { id, label, layout, helperText, infoTooltipText, validations } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isReadOnly = field.isReadOnly || isReadOnlyProp || false;

  const options: RenderCheckboxOption[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const { fixed } = (dataProvider as { fixed?: ProviderOption[] }) || {};

    return Array.isArray(fixed) ? fixed.map(mapProviderOption) : [];
  }, [dataProvider]);

  if (options.length === 0) {
    return null;
  }

  const groupClasses = cx(
    styles.checkboxGroup,
    layout === 'vertical' && styles.checkboxGroup__isVertical,
    layout === 'horizontal' && styles.checkboxGroup__isHorizontal
  );

  const shouldRequiredAsterisk = isRequired && value.length === 0;

  const groupBottomText = error ?? helperText;

  return (
    <Box className={styles.checkbox}>
      <Box>
        <FieldLabel
          label={label}
          isRequired={isRequired}
          isCritical={!!error}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
          shouldRequiredAsterisk={shouldRequiredAsterisk}
        />
      </Box>
      <Box
        className={groupClasses}
        dataTestId={testIds.FF_PREVIEW_CHECKBOX_FIELD}>
        {options.map((option) => {
          const checked =
            value.includes(option.label) || value.includes(option.value);

          return (
            <DSCheckbox
              key={option.value}
              id={`${id}-${option.value}`}
              kind='singleLine'
              label={option.label || '-'}
              labelLineLimit={4}
              isChecked={checked}
              isDisabled={isReadOnly}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const willCheck = e.target.checked;

                if (!onChange) {
                  return;
                }

                const filtered = value.filter(
                  (v) => v !== option.label && v !== option.value
                );

                onChange(willCheck ? [...filtered, option.label] : filtered);
              }}
              className={styles.checkboxItem}
              classNames={{
                labelClassName: cx(isReadOnly && styles.value__isReadOnly),
              }}
            />
          );
        })}
      </Box>
      {(error || helperText) && (
        <InlineMessage
          text={groupBottomText}
          colorTheme={error ? 'critical' : 'neutral'}
          size='small'
          leftIcon={error ? <Icon kind='AlertCriticalFilled' /> : undefined}
        />
      )}
    </Box>
  );
}

export default Checkbox;
